import { faEllipsisV } from "@fortawesome/pro-regular-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Link, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcChipChoice, MprcContainer, MprcIconButtonDropdown, MprcRequestIndicator, MprcSpinnerIndicator, MprcTypography } from 'components';
import { MprcDropdownItem } from 'components/MprcIconButtonDropdown';
import { MprcRibbonScroll } from "components/MprcRibbonScroll";
import { GetRelatedPublicationsResponse, PublicationClassificationType, PublicationType } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router";
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { LmsPaths } from "routes/LmsRouter";
import { AddToListDrawer } from 'views/knowledge/components/card/actions/AddToListDrawer';
import { subscribePublishRelatedContent, unsubscribePublishRelatedContent } from "views/knowledge/components/card/CardEvents";
import { useAddPublicationToSeeLaterList } from "views/knowledge/hooks/useAddPublicationToSeeLaterList";

////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
}

type MenuOptions = "addSeeLaterList" | "addAnotherList";

enum KnowledgePublicationRelatedType { All, Publication, Training, Video, List, Question }

////////////////////////////////////////////////////////////////////////////////////////////////

export const SeeAlsoBox: React.FC<IProps> = ({ publicationId }) => {

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();
    const [addToSeeLater, postingSeeLater] = useAddPublicationToSeeLaterList();

    const [data, setData] = useState<GetRelatedPublicationsResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [limit, setLimit] = useState<number>(5);
    const [dialog, setDialog] = useState<any>();
    const [option, setOption] = useState<KnowledgePublicationRelatedType>(KnowledgePublicationRelatedType.All);

    ///
    const getMenuItems = useCallback((item: GetRelatedPublicationsResponse): MprcDropdownItem<MenuOptions>[] =>
        [
            { text: t("Publication.ContentPage.AddToSeeLaterAction"), value: "addSeeLaterList" },
            { text: t("Publication.ContentPage.AddToAnotherListAction"), value: "addAnotherList" }
        ], []);

    ///
    const handleMenuItem = (option: MenuOptions, item: GetRelatedPublicationsResponse) => {
        switch (option) {
            case "addSeeLaterList":
                addToSeeLater(item.id);
                break;
            case "addAnotherList":
                setDialog(
                    <AddToListDrawer
                        publicationId={item.id}
                        onChanged={() => setDialog(null)}
                        subtitle={item.title}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
        }
    };

    ///
    const classifications = useMemo(() => {
        return {
            [KnowledgePublicationRelatedType.Training]: data?.filter(p => p.trainingContentType != null).length ?? 0,
            [KnowledgePublicationRelatedType.Publication]: data.filter(p => p.publicationType != null).length ?? 0,
            [KnowledgePublicationRelatedType.Video]: data.filter(p => p.publicationClassificationType == PublicationClassificationType.Video).length ?? 0,
            [KnowledgePublicationRelatedType.Question]: data.filter(p => p.publicationClassificationType == PublicationClassificationType.Question).length ?? 0,
            [KnowledgePublicationRelatedType.List]: data.filter(p => p.publicationClassificationType == PublicationClassificationType.List).length ?? 0
        };
    }, [data]);

    ///
    const getData = useMemo(() => {
        return data
            .filter((item) =>
                (option == KnowledgePublicationRelatedType.All)
                ||
                (option == KnowledgePublicationRelatedType.List && item.publicationType == PublicationType.List)
                ||
                (option == KnowledgePublicationRelatedType.Question && item.publicationType == PublicationType.Question)
                ||
                (option == KnowledgePublicationRelatedType.Video && item.publicationClassificationType == PublicationClassificationType.Video)
                ||
                (option == KnowledgePublicationRelatedType.Training && item.trainingContentType != null)
                ||
                (option == KnowledgePublicationRelatedType.Publication && item.publicationType != null)
            );
    }, [option, data]);

    ///
    useEffect(() => {
        setLimit(5);
    }, [option]);

    ///
    useEffect(() => {
        function load() {
            setLoading(true);

            api.v1.knowledgePublication.getRelatedPublications({ publicationId: publicationId })
                .then((result) => {
                    setData(result.data);
                })
                .catch(() => {
                    setData([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        if (loading) load();
        subscribePublishRelatedContent(load);

        return () => {
            unsubscribePublishRelatedContent(load);
        };

    }, [loading]);

    return loading && data.length == 0
        ? <MprcContainer><MprcSpinnerIndicator /></MprcContainer>
        : data.length > 0
            ? <>
                {dialog}
                <MprcRequestIndicator visible={postingSeeLater} type="post" />

                <MprcContainer margin="dense">
                    <Typography variant="h3">{t("Publication.ContentPage.SeeAlsoSubtitle")}</Typography>
                </MprcContainer>

                {data.length > 0 && Object.values(classifications).filter(p => p > 0).length > 1 &&
                    <MprcContainer margin="dense">
                        <MprcRibbonScroll>
                            <MprcChipChoice
                                onClick={() => setOption(KnowledgePublicationRelatedType.All)}
                                label={t("Publication.ContentPage.SeeAlso.All")}
                                selected={option == KnowledgePublicationRelatedType.All}
                            />
                            {classifications[KnowledgePublicationRelatedType.Publication] > 0 &&
                                <MprcChipChoice
                                    onClick={() => setOption(KnowledgePublicationRelatedType.Publication)}
                                    label={t("Publication.ContentPage.SeeAlso.Publications")}
                                    selected={option == KnowledgePublicationRelatedType.Publication}
                                />
                            }
                            {classifications[KnowledgePublicationRelatedType.Training] > 0 &&
                                <MprcChipChoice
                                    onClick={() => setOption(KnowledgePublicationRelatedType.Training)}
                                    label={t("Publication.ContentPage.SeeAlso.Trainings")}
                                    selected={option == KnowledgePublicationRelatedType.Training}
                                />
                            }
                            {classifications[KnowledgePublicationRelatedType.Video] > 0 &&
                                <MprcChipChoice
                                    onClick={() => setOption(KnowledgePublicationRelatedType.Video)}
                                    label={t("Publication.ContentPage.SeeAlso.Videos")}
                                    selected={option == KnowledgePublicationRelatedType.Video}
                                />
                            }
                            {classifications[KnowledgePublicationRelatedType.Question] > 0 &&
                                <MprcChipChoice
                                    onClick={() => setOption(KnowledgePublicationRelatedType.Question)}
                                    label={t("Publication.ContentPage.SeeAlso.Questions")}
                                    selected={option == KnowledgePublicationRelatedType.Question}
                                />
                            }
                            {classifications[KnowledgePublicationRelatedType.List] > 0 &&
                                <MprcChipChoice
                                    onClick={() => setOption(KnowledgePublicationRelatedType.List)}
                                    label={t("Publication.ContentPage.SeeAlso.Lists")}
                                    selected={option == KnowledgePublicationRelatedType.List}
                                />
                            }
                        </MprcRibbonScroll>
                    </MprcContainer>
                }
                <Box sx={{ overflowY: "auto", maxHeight: "248px" }} className={AppGlobalClassKeys.thinScrollbar}>
                    <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="flex-start" alignContent="flex-start">
                        {
                            loading
                                ? <MprcSpinnerIndicator />
                                : (
                                    <>
                                        {
                                            getData
                                                .filter((_, i) => i < limit)
                                                .map((item) =>
                                                    <Grid container key={item.id} alignItems="start" justifyContent={"space-between"} width="100%">
                                                        <Grid size="grow">
                                                            <Typography variant="captionHeader">{item.classificationName}</Typography>
                                                            <Link
                                                                onClick={
                                                                    () =>
                                                                        item.publicationType != null
                                                                            ? navigate(KnowledgePaths.PublicationViewerResolver(item.id, item.publicationType))
                                                                            : navigate(LmsPaths.BuildTrainingViewer(item.id))
                                                                }
                                                            >
                                                                <MprcTypography variant="h6" noWrapRows={2}>{item.title}</MprcTypography>
                                                            </Link>
                                                        </Grid>
                                                        {
                                                            item.publicationType != null &&
                                                            <Grid>
                                                                <MprcIconButtonDropdown
                                                                    buttonSize="small"
                                                                    icon={<FontAwesomeIcon icon={faEllipsisV} />}
                                                                    items={getMenuItems(item)}
                                                                    onChange={(option) => handleMenuItem(option, item)}
                                                                />
                                                            </Grid>
                                                        }
                                                    </Grid>
                                                )
                                        }
                                        {
                                            getData.length > limit &&
                                            <Link onClick={() => setLimit(Number.MAX_SAFE_INTEGER)}>
                                                <MprcTypography variant="captionHeader" textColor="primary"
                                                    textColorVariant="main">{t("Publication.ContentPage.ViewMoreText")}</MprcTypography>
                                            </Link>
                                        }
                                        {
                                            limit == Number.MAX_SAFE_INTEGER &&
                                            <Link onClick={() => setLimit(5)}>
                                                <MprcTypography variant="captionHeader" textColor="primary"
                                                    textColorVariant="main">{t("Publication.ContentPage.ViewLessText")}</MprcTypography>
                                            </Link>
                                        }
                                    </>
                                )
                        }
                    </Stack >
                </Box>
            </>
            : null;
};
